import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../context/AuthContext';
import axios from '../api/axios';
import { FaUser, FaLock, FaBuilding, FaEnvelope, FaKey, FaGraduationCap, FaLayerGroup, FaChalkboardTeacher, FaCheckCircle } from 'react-icons/fa';
import { SuccessModal, ErrorModal, LoadingModal } from './SharedComponents/Modals';

const Profile = () => {
    const { auth } = useContext(AuthContext);
    
    // Stati Dati Profilo
    const [enrollments, setEnrollments] = useState({ courses: [], groups: [], classes: [] });
    const [loadingData, setLoadingData] = useState(true);

    // Stati Azioni
    const [passData, setPassData] = useState({ current: '', new: '', confirm: '' });
    const [enrollCode, setEnrollCode] = useState('');
    
    // Stati Feedback
    const [modals, setModals] = useState({ success: false, error: false, loading: false });
    const [statusMsg, setStatusMsg] = useState({ title: '', msg: '' });

    // --- 1. FETCH DATI PROFILO (Gruppi, Corsi, Classi) ---
    const fetchProfileData = async () => {
        try {
            const res = await axios.get(`/api_profile_data.php?user_id=${auth.user_id}`);
            if (res.data.success) {
                setEnrollments(res.data.data);
            }
        } catch (e) { console.error("Errore fetch profilo", e); }
        finally { setLoadingData(false); }
    };

    useEffect(() => { fetchProfileData(); }, []);

    // --- 2. CAMBIO PASSWORD ---
    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        if (passData.new !== passData.confirm) {
            setStatusMsg({ title: 'Errore', msg: 'Le nuove password non coincidono.' });
            setModals({ ...modals, error: true });
            return;
        }
        if (passData.new.length < 6) {
            setStatusMsg({ title: 'Errore', msg: 'La password deve essere di almeno 6 caratteri.' });
            setModals({ ...modals, error: true });
            return;
        }

        setModals({ ...modals, loading: true });
        try {
            const response = await axios.post('/api_profile.php', {
                action: 'change_password',
                user_id: auth.user_id,
                current_password: passData.current,
                new_password: passData.new
            });

            setModals({ ...modals, loading: false });
            if (response.data.success) {
                setStatusMsg({ title: 'Successo', msg: 'Password aggiornata con successo!' });
                setModals(prev => ({ ...prev, success: true }));
                setPassData({ current: '', new: '', confirm: '' });
            } else {
                setStatusMsg({ title: 'Errore', msg: response.data.message });
                setModals(prev => ({ ...prev, error: true }));
            }
        } catch (error) {
            setModals({ ...modals, loading: false });
            setStatusMsg({ title: 'Errore Server', msg: 'Impossibile aggiornare la password.' });
            setModals(prev => ({ ...prev, error: true }));
        }
    };

    // --- 3. ISCRIZIONE A UN CORSO (ENROLLMENT) ---
    const handleEnrollment = async (e) => {
        e.preventDefault();
        if (!enrollCode.trim()) return;

        setModals({ ...modals, loading: true });
        try {
            const res = await axios.post('/api_enrollment.php', {
                user_id: auth.user_id,
                code: enrollCode.trim().toUpperCase()
            });

            setModals({ ...modals, loading: false });
            if (res.data.success) {
                setStatusMsg({ title: 'Benvenuto!', msg: `Ti sei iscritto con successo al corso: ${res.data.course_name}` });
                setModals(prev => ({ ...prev, success: true }));
                setEnrollCode('');
                fetchProfileData(); // Aggiorna la lista
            } else {
                setStatusMsg({ title: 'Attenzione', msg: res.data.message });
                setModals(prev => ({ ...prev, error: true }));
            }
        } catch (error) {
            setModals({ ...modals, loading: false });
            setStatusMsg({ title: 'Errore', msg: 'Codice non valido o errore di connessione.' });
            setModals(prev => ({ ...prev, error: true }));
        }
    };

    return (
        <div className="container-fluid fade-in pb-5">
            <h4 className="text-primary fw-bold mb-4">Il mio Profilo</h4>

            <div className="row g-4 mb-5">
                {/* A. DATI PERSONALI */}
                <div className="col-md-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white fw-bold text-muted"><FaUser className="me-2" /> Dati Personali</div>
                        <div className="card-body">
                            <div className="mb-3">
                                <label className="small text-muted">Nome Completo</label>
                                <div className="fw-bold fs-5">{auth.name}</div>
                            </div>
                            <div className="mb-3">
                                <label className="small text-muted">Email</label>
                                <div className="d-flex align-items-center"><FaEnvelope className="me-2 text-primary opacity-50" />{auth.email}</div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="col-6">
                                    <label className="small text-muted">Ruolo</label>
                                    <div><span className="badge bg-secondary text-uppercase">{auth.role}</span></div>
                                </div>
                                <div className="col-6">
                                    <label className="small text-muted">Azienda</label>
                                    <div className="d-flex align-items-center"><FaBuilding className="me-2 text-primary opacity-50" />{auth.tenant_name || 'N/A'}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* B. SICUREZZA (Cambio Password) */}
                <div className="col-md-6">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white fw-bold text-muted"><FaLock className="me-2" /> Sicurezza</div>
                        <div className="card-body">
                            <form onSubmit={handlePasswordUpdate}>
                                <div className="mb-2">
                                    <label className="form-label small">Password Attuale</label>
                                    <input type="password" className="form-control form-control-sm" required autoComplete="current-password" value={passData.current} onChange={e => setPassData({...passData, current: e.target.value})} />
                                </div>
                                <div className="row g-2 mb-3">
                                    <div className="col-6">
                                        <label className="form-label small">Nuova Password</label>
                                        <input type="password" className="form-control form-control-sm" required minLength="6" autoComplete="new-password" value={passData.new} onChange={e => setPassData({...passData, new: e.target.value})} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label small">Conferma</label>
                                        <input type="password" className="form-control form-control-sm" required minLength="6" autoComplete="new-password" value={passData.confirm} onChange={e => setPassData({...passData, confirm: e.target.value})} />
                                    </div>
                                </div>
                                <div className="text-end">
                                    <button type="submit" className="btn btn-primary btn-sm px-4">Aggiorna Password</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* C. ISCRIZIONE CORSO */}
            <div className="card shadow-sm border-0 mb-5 bg-soft-primary">
                <div className="card-body p-4">
                    <div className="row align-items-center">
                        <div className="col-md-8">
                            <h5 className="fw-bold text-primary mb-1"><FaGraduationCap className="me-2"/>Unisciti a un Corso</h5>
                            <p className="text-muted small mb-0">Hai un codice di iscrizione? Inseriscilo qui per accedere immediatamente ai materiali del corso.</p>
                        </div>
                        <div className="col-md-4 mt-3 mt-md-0">
                            <form onSubmit={handleEnrollment} className="d-flex gap-2">
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Codice Corso (es. A1B2C3D4)" 
                                    value={enrollCode} 
                                    onChange={e => setEnrollCode(e.target.value.toUpperCase())} 
                                    maxLength="8"
                                    required
                                />
                                <button type="submit" className="btn btn-primary fw-bold px-4"><FaKey className="me-2"/>Unisciti</button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/* D. LE MIE ISCRIZIONI (Corsi, Gruppi, Classi) */}
            <h5 className="text-muted fw-bold mb-3"><FaCheckCircle className="me-2"/>Le mie abilitazioni</h5>
            <div className="row g-4">
                {/* 1. CORSI */}
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white fw-bold text-primary"><FaGraduationCap className="me-2"/>Corsi Iscritti</div>
                        <div className="list-group list-group-flush">
                            {enrollments.courses.length > 0 ? enrollments.courses.map(c => (
                                <div key={c.id} className="list-group-item">
                                    <div className="fw-bold">{c.name}</div>
                                    <small className="text-muted">Iscritto il: {new Date(c.enrolled_at).toLocaleDateString()}</small>
                                </div>
                            )) : <div className="p-3 text-center text-muted small">Nessun corso.</div>}
                        </div>
                    </div>
                </div>

                {/* 2. GRUPPI */}
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white fw-bold text-success"><FaLayerGroup className="me-2"/>Gruppi di Appartenenza</div>
                        <div className="list-group list-group-flush">
                            {enrollments.groups.length > 0 ? enrollments.groups.map(g => (
                                <div key={g.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span className="fw-bold">{g.name}</span>
                                    <span className="badge bg-light text-secondary border">Membro</span>
                                </div>
                            )) : <div className="p-3 text-center text-muted small">Nessun gruppo.</div>}
                        </div>
                    </div>
                </div>

                {/* 3. CLASSI SPECIALI (Permessi) */}
                <div className="col-md-4">
                    <div className="card shadow-sm border-0 h-100">
                        <div className="card-header bg-white fw-bold text-warning"><FaChalkboardTeacher className="me-2"/>Classi Speciali (Permessi)</div>
                        <div className="list-group list-group-flush">
                            {enrollments.classes.length > 0 ? enrollments.classes.map(c => (
                                <div key={c.id} className="list-group-item">
                                    <div className="fw-bold text-dark">{c.name}</div>
                                    <small className="text-muted">Accesso speciale garantito</small>
                                </div>
                            )) : <div className="p-3 text-center text-muted small">Nessun permesso speciale.</div>}
                        </div>
                    </div>
                </div>
            </div>

            {/* FEEDBACK */}
            <SuccessModal show={modals.success} title={statusMsg.title} message={statusMsg.msg} onClose={() => setModals(prev => ({...prev, success: false}))} />
            <ErrorModal show={modals.error} title={statusMsg.title} message={statusMsg.msg} onClose={() => setModals(prev => ({...prev, error: false}))} />
            <LoadingModal show={modals.loading} message="Elaborazione..." />
        </div>
    );
};

export default Profile;